import { useTranslation } from 'react-i18next';
import React, { memo, useCallback, useState, useMemo, useEffect } from 'react';
import ReactGA4 from 'react-ga4';
import cx from 'clsx';
import { ImgRcVipLabel, ImgRcSendGift } from '@/assets/pro';
import { Button, DesktopHeader2, HelperTooltip } from '@/components';
import { formatDate, login } from '@/utils';
import {
  useCurrentAddressInfo,
  useSwitch,
  useUserDetail,
  useQueryUserPaidList,
  useQueryPaidFeatureList,
  PAID_FEATURE,
  getGaUserType,
} from '@/hooks';
import { PaidFeatureModal } from './components/PaidFeatureModal';
import style from './index.module.scss';

const formatPrice = (price: number) => {
  return (price / 10000).toFixed(0);
};

export const Container = memo(() => {
  const { t } = useTranslation();

  const TIP_LIST = useMemo(
    () => [t('Pro_Container.t1'), t('Pro_Container.t2'), t('Pro_Container.t3')],
    [t],
  );

  const { address } = useCurrentAddressInfo();
  const { data: userInfo } = useUserDetail(address);

  const isVip = useMemo(() => userInfo?.is_vip, [userInfo?.is_vip]);
  const { on: onPaidModal, turnOn: turnOnPaidModal, turnOff: turnOffPaidModal } = useSwitch();
  const [selectedFeatureItem, setSelectedFeatureItem] = useState<PaidFeatureItem>();
  const [sendOther, setSendOther] = useState<boolean>(false);
  const { data: mySubscriptions } = useQueryUserPaidList();
  const { data: featuresConfig } = useQueryPaidFeatureList();

  const commonFeaturesConfig = useMemo(() => {
    return featuresConfig?.filter(item => item.id !== PAID_FEATURE.VIP);
  }, [featuresConfig]);

  const vipFeatureConfig = useMemo(() => {
    return featuresConfig?.find(item => item.id === PAID_FEATURE.VIP);
  }, [featuresConfig]);

  const totalCommonFeaturePrice = useMemo(() => {
    return commonFeaturesConfig?.reduce((acc, cur) => acc + cur.price, 0);
  }, [commonFeaturesConfig]);

  const startSubscription = useCallback(
    (item: PaidFeatureItem, sendOther: boolean = false) => {
      if (!address) {
        login();
        return;
      }
      setSendOther(sendOther);
      setSelectedFeatureItem(item);
      turnOnPaidModal();
    },
    [address, turnOnPaidModal],
  );

  useEffect(() => {
    ReactGA4.event({
      category: 'View Paid Features',
      action: `Paid Features_${getGaUserType()}`,
    });
  }, []);

  return (
    <>
      <DesktopHeader2 title={t('Pro_Container.u72fpt')} className={style.header} />
      <div className={style.container}>
        <div className={style.desc}>{t('Pro_Container.67vxoh')}</div>
        {mySubscriptions?.length ? (
          <div className={style.mySubscriptions}>
            <div className={style.title}>My Subscriptions</div>
            <div className={style.mySubscriptionList}>
              {mySubscriptions.map(item => (
                <div className={style.mySubscriptionItem} key={item.id}>
                  <div className={style.name}>{item.name}</div>
                  <div className={style.time}>
                    {t('Pro_Container.v96jcp')} {formatDate(item.expired_at * 1000, 'YYYY/MM/DD')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className={style.featureTable}>
          <div className={style.featureCol}>
            <div className={style.headerCell}>{t('Pro_Container.bc3hee')}</div>
            {commonFeaturesConfig?.map(item => (
              <div className={style.itemCell} key={item.id}>
                <span className={style.name}>{item.name}</span>

                {/* {item.url ? (
                  <a className={style.url} href={item.url} target="_blank" rel="noreferrer">
                    Learn more
                  </a>
                ) : ( */}
                <span className={style.desc}>{item.desc}</span>
                {/* )} */}
              </div>
            ))}
            <div className={style.itemCell}>
              <span>{t('Pro_Container.58dplp')}</span>
            </div>
          </div>

          <div className={style.priceCol}>
            <div className={style.headerCell}>{t('Pro_Container.1a2ka1')}</div>
            {commonFeaturesConfig?.map(item => (
              <div className={style.itemCell} key={item.id}>
                <div>
                  <span className={style.price}>${formatPrice(item.price)}</span>
                  <span className={style.durations}>/{t('Pro_Container.nz3169')}</span>
                </div>
                {isVip ? null : (
                  <HelperTooltip disable={item.is_subscribable} title={t('Pro_Container.bulkTip')}>
                    <Button
                      type="primary"
                      disabled={!item.is_subscribable}
                      className={style.subscriptionItemButton}
                      onClick={() => startSubscription(item, false)}>
                      {t('Pro_Container.15nmu5m')}
                    </Button>
                  </HelperTooltip>
                )}
              </div>
            ))}
            <div className={style.itemCell}></div>
          </div>

          <div className={style.subscriptionCol}>
            <div className={style.headerCell}>
              <span className={style.subscriptionTitle}>{vipFeatureConfig?.name}</span>
            </div>
            <div className={style.subscriptionCell}>
              <div className={style.realPrice}>
                ${formatPrice(vipFeatureConfig?.price || 0)}
                <span className={style.durations}>{t('Pro_Container.nz3169')}*</span>
              </div>
              <div className={style.originalPrice}>
                <span className={style.lineThrough}>
                  ${formatPrice(totalCommonFeaturePrice || 0)}
                </span>
                <span className={style.durations}>{t('Pro_Container.nz3169')}*</span>
              </div>
              <div className={style.bonusTitle}>{t('Pro_Container.bulkBonus')}</div>
              <div className={style.bonusDesc}>
                <ImgRcVipLabel className={style.label} />
                {t('Pro_Container.bulkDesc')}
              </div>
              <Button
                type="primary"
                className={style.subscriptionButton}
                disabled={!vipFeatureConfig}
                onClick={() => startSubscription(vipFeatureConfig!, false)}>
                {t('Pro_Container.15nmu5m')}
              </Button>

              <div
                className={style.sendGiftButton}
                onClick={() => startSubscription(vipFeatureConfig!, true)}>
                <ImgRcSendGift />
                <div className={style.sendGiftText}>
                  {t('Pro_Container.1jb5vjw')}
                  <div className={style.line}></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={style.footer}>
          {TIP_LIST.map((item, idx) => (
            <div className={cx(style.tip)} key={idx}>
              {item}
            </div>
          ))}
        </div>
        {onPaidModal && selectedFeatureItem ? (
          <PaidFeatureModal
            featureItem={selectedFeatureItem}
            sendOther={sendOther}
            turnOff={turnOffPaidModal}
          />
        ) : null}
      </div>
    </>
  );
});
